import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { callAPIWithoutAuth } from '../../utils/apiUtils';
import { apiUrls } from '../../utils/apiUrls';

export const fetchFeaturedProducts = createAsyncThunk(
    'product/fetchFeaturedProducts',
    async (limit = 10, { rejectWithValue }) => {
        try {
            const params = { limit, is_featured: "yes" };
            const response = await callAPIWithoutAuth(
                apiUrls.featuredProductListing,
                params,
                "GET",
                {}
            );
            if (response?.data?.success) {
                return response.data.data.data || [];
            } else {
                return rejectWithValue(response?.data?.message || 'Failed to fetch featured products');
            }
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || 'Network error');
        }
    }
);

export const fetchBestSellingProducts = createAsyncThunk(
    'product/fetchBestSellingProducts',
    async (limit = 10, { rejectWithValue }) => {
        try {
            const params = { limit, sort: 'sales', order: 'desc' };
            const response = await callAPIWithoutAuth(
                apiUrls.bestSellingProducts,
                params,
                "GET",
                {}
            );

            if (response?.data?.success) {
                return response.data.data.data || [];
            } else {
                return rejectWithValue(response?.data?.message || 'Failed to fetch best selling products');
            }
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || 'Network error');
        }
    }
);

export const fetchNewArrivals = createAsyncThunk(
    'product/fetchNewArrivals',
    async (limit = 10, { rejectWithValue }) => {
        try {
            const params = { limit, sort: 'created_at', order: 'desc' };
            const response = await callAPIWithoutAuth(
                apiUrls.newArrivals,
                params,
                "GET",
                {}
            );

            if (response?.data?.success) {
                return response.data.data.data || [];
            } else {
                return rejectWithValue(response?.data?.message || 'Failed to fetch new arrivals');
            }
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || 'Network error');
        }
    }
);

export const fetchRelatedProducts = createAsyncThunk(
    'product/fetchRelatedProducts',
    async ({ productId, limit = 5 }, { rejectWithValue }) => {
        try {
            const params = { product_id: productId, limit };
            const response = await callAPIWithoutAuth(
                apiUrls.relatedProducts,
                params,
                "GET",
                {}
            );

            if (response?.data?.success) {
                return response.data.data.data || [];
            } else {
                return rejectWithValue(response?.data?.message || 'Failed to fetch related products');
            }
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || 'Network error');
        }
    }
);

const productSlice = createSlice({
    name: 'products',
    initialState: {
        featuredProducts: [],
        bestSellingProducts: [],
        newArrivals: [],
        relatedProducts: [],
        featuredLoading: false,
        bestSellingLoading: false,
        newArrivalsLoading: false,
        relatedLoading: false,
        error: null
    },
    reducers: {
        clearProductError: (state) => {
            state.error = null;
        },
        clearProducts: (state) => {
            state.featuredProducts = [];
            state.bestSellingProducts = [];
            state.newArrivals = [];
            state.relatedProducts = [];
        },
        updateProductFavoriteStatus: (state, action) => {
            const { productId, isFavorite } = action.payload;

            if (Array.isArray(state.featuredProducts)) {
                state.featuredProducts = state.featuredProducts.map(product =>
                    product && product.id == productId
                        ? { ...product, isFavorite, is_saved: isFavorite }
                        : product
                );
            }

            if (Array.isArray(state.bestSellingProducts)) {
                state.bestSellingProducts = state.bestSellingProducts.map(product =>
                    product && product.id == productId
                        ? { ...product, isFavorite, is_saved: isFavorite }
                        : product
                );
            }

            if (Array.isArray(state.newArrivals)) {
                state.newArrivals = state.newArrivals.map(product =>
                    product && product.id == productId
                        ? { ...product, isFavorite, is_saved: isFavorite }
                        : product
                );
            }

            if (Array.isArray(state.relatedProducts)) {
                state.relatedProducts = state.relatedProducts.map(product =>
                    product && product.id == productId
                        ? { ...product, isFavorite, is_saved: isFavorite }
                        : product
                );
            }
        }
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchFeaturedProducts.pending, (state) => {
                state.featuredLoading = true;
                state.error = null;
            })
            .addCase(fetchFeaturedProducts.fulfilled, (state, action) => {
                state.featuredLoading = false;
                state.featuredProducts = Array.isArray(action.payload) ? action.payload : [];
            })
            .addCase(fetchFeaturedProducts.rejected, (state, action) => {
                state.featuredLoading = false;
                state.error = action.payload;
                state.featuredProducts = [];
            })

            .addCase(fetchBestSellingProducts.pending, (state) => {
                state.bestSellingLoading = true;
                state.error = null;
            })
            .addCase(fetchBestSellingProducts.fulfilled, (state, action) => {
                state.bestSellingLoading = false;
                state.bestSellingProducts = Array.isArray(action.payload) ? action.payload : [];
            })
            .addCase(fetchBestSellingProducts.rejected, (state, action) => {
                state.bestSellingLoading = false;
                state.error = action.payload;
                state.bestSellingProducts = [];
            })

            .addCase(fetchNewArrivals.pending, (state) => {
                state.newArrivalsLoading = true;
                state.error = null;
            })
            .addCase(fetchNewArrivals.fulfilled, (state, action) => {
                state.newArrivalsLoading = false;
                state.newArrivals = Array.isArray(action.payload) ? action.payload : [];
            })
            .addCase(fetchNewArrivals.rejected, (state, action) => {
                state.newArrivalsLoading = false;
                state.error = action.payload;
                state.newArrivals = [];
            })

            .addCase(fetchRelatedProducts.pending, (state) => {
                state.relatedLoading = true;
                state.error = null;
            })
            .addCase(fetchRelatedProducts.fulfilled, (state, action) => {
                state.relatedLoading = false;
                state.relatedProducts = Array.isArray(action.payload) ? action.payload : [];
            })
            .addCase(fetchRelatedProducts.rejected, (state, action) => {
                state.relatedLoading = false;
                state.error = action.payload;
                state.relatedProducts = [];
            });
    }
});

export const { clearProductError, clearProducts, updateProductFavoriteStatus } = productSlice.actions;
export default productSlice.reducer;